import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { OrderItemResponse, OrderResponse, PaymentRecord } from '@/types/order-type';
import { calculateOrderTotals } from '@/utils/calc-order';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { CreditCard, Settings } from 'lucide-react';
import { useMemo } from 'react';
import Iconify from '../iconify/iconify';

import { Box, Typography } from '@mui/material';
import PaymentRecordLine from './PaymentRecordLine';
import { SummaryChip } from './SummaryChip';

interface PaymentInfoCardProps {
  order: OrderResponse;
  orderItems: OrderItemResponse[];
  payments: PaymentRecord[];
  onMarkCOD?: () => void;
  onMarkDraft?: () => void;
  onCapturePayment?: () => void;
}

export default function PaymentInfoCard({
  order,
  orderItems,
  payments,
  onMarkCOD,
  onMarkDraft,
  onCapturePayment,
}: PaymentInfoCardProps) {
  const { total } = useMemo(() => {
    return calculateOrderTotals({
      items: orderItems,
      deliveryFee: Number(order.deliveryFee ?? 2),
      discountPercentage: Number(order.discountPercentage ?? 0),
      taxRate: 0, // optional
    });
  }, [orderItems, order.deliveryFee, order.discountPercentage]);
  const isPaid = order.status === 'PAID' || order.paymentStatus === 'PAID';

  payments = payments || [];
  const totalPaid = useMemo(
    () => payments.reduce((sum, p) => sum + (p.amount || 0), 0),
    [payments],
  );

  const remaining = Math.max(total - totalPaid, 0);

  const paymentStatus = (order.paymentStatus || 'UNPAID').toString();
  const statusStyle = getPaymentStatusStyle(paymentStatus);

  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
      <CardHeader className="flex items-center justify-between py-3 px-3 sm:px-4">
        <div className="flex w-full items-center justify-between gap-2 sm:gap-4">
          {/* Left: Title + status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              flexWrap: 'wrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            <CreditCard className="h-4 w-4 text-slate-700 flex-shrink-0" />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                color: 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              Payment
            </Typography>

            <Badge
              variant={statusStyle.badge}
              className={cn('border text-[10px] sm:text-[11px] font-semibold px-1.5 sm:px-2 py-0.5 flex-shrink-0', statusStyle.cls)}
            >
              {paymentStatus}
            </Badge>
          </Box>

          {/* Right: Settings Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 float-right"
                title="Order Actions"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="z-50 w-60 bg-white shadow-lg border border-gray-200 rounded-lg p-1"
            >
              <DropdownMenuItem
                onClick={!isPaid ? onMarkCOD : undefined}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 text-[14px] font-medium rounded-md transition-all',
                  isPaid
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50 hover:bg-gray-50'
                    : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 cursor-pointer',
                )}
              >
                <Iconify
                  icon="mdi:cash"
                  className={cn('h-5 w-5', isPaid ? 'text-gray-300' : 'text-emerald-500')}
                />
                Mark as Cash on Delivery
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={!isPaid ? onCapturePayment : undefined}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 text-[14px] font-medium rounded-md transition-all',
                  isPaid
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50 hover:bg-gray-50'
                    : 'text-gray-700 hover:bg-green-50 hover:text-green-700 cursor-pointer',
                )}
              >
                <Iconify
                  icon="mdi:credit-card-check-outline"
                  className={cn('h-5 w-5', isPaid ? 'text-gray-300' : 'text-green-500')}
                />
                Capture Payment
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* MARK AS DRAFT */}
              {/* <DropdownMenuItem
                onClick={onMarkDraft}
                className="flex items-center gap-3 px-4 py-3 text-[14px] font-medium
              text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-md cursor-pointer transition-all"
              >
                <Iconify icon="mdi:pencil-outline" className="h-5 w-5 text-blue-500" />
                Mark as Draft
              </DropdownMenuItem> */}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-3 overflow-x-hidden min-w-0">
        {/* COD banner */}
        {paymentStatus === 'COD' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 sm:px-3 py-2 text-[11px] sm:text-[12px] text-amber-800">
            <span className="font-semibold mr-1">Cash on Delivery.</span>
            Payment will be collected when the order arrives. Remember to capture the payment at
            that time.
          </div>
        )}

        {/* Totals row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm min-w-0">
          <SummaryChip label="Total" value={total} type="neutral" />
          <SummaryChip label="Paid" value={totalPaid} type="paid" />
          <SummaryChip label="Remaining" value={remaining} type="remaining" />
          {order.refundAmount && (
            <SummaryChip label="Refund" value={order.refundAmount!} type="refund" />
          )}
        </div>

        {/* Payment history list */}
        <div className="mt-2 min-w-0">
          <div className="flex items-center justify-between mb-1 gap-2 min-w-0">
            <span className="text-[10px] sm:text-[11px] font-semibold text-slate-500 uppercase tracking-wide truncate min-w-0">
              Payment History
            </span>
            {payments.length > 0 && (
              <span className="text-[10px] sm:text-[11px] text-slate-400 whitespace-nowrap">
                {payments.length} record{payments.length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {payments.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-2.5 sm:px-3 py-2.5 sm:py-3 text-[11px] sm:text-[12px] text-slate-500 text-center">
              No payments captured yet. Use <span className="font-semibold">Capture Payment</span>{' '}
              to record a payment for this order.
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto overflow-x-hidden space-y-2 min-w-0">
              {payments.map((payment) => (
                <PaymentRecordLine key={payment.id} payment={payment} />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function getPaymentStatusStyle(status: string) {
  switch (status.toUpperCase()) {
    case 'PAID':
      return {
        badge: 'secondary' as const,
        cls: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      };

    case 'PARTIAL':
    case 'PARTIALLY_PAID':
      return {
        badge: 'outline' as const,
        cls: 'bg-amber-50 border-amber-300 text-amber-800',
      };

    case 'COD':
      return {
        badge: 'outline' as const,
        cls: 'bg-blue-50 border-blue-300 text-blue-700',
      };

    case 'REFUNDED':
      return {
        badge: 'outline' as const,
        cls: 'bg-rose-50 border-rose-300 text-rose-700',
      };

    case 'PARTIALLY_REFUNDED':
      return {
        badge: 'outline' as const,
        cls: 'bg-orange-50 border-orange-300 text-orange-700',
      };

    case 'UNPAID':
    default:
      return {
        badge: 'outline' as const,
        cls: 'bg-slate-50 border-slate-300 text-slate-700',
      };
  }
}
